import React, { Suspense, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Router, Route, Switch, useParams } from "react-router-dom";
import { DesignCore } from "ui-components";
import history from "utils/history";
import { routes, publicRoutes, authorizedRoutes } from "../../routes";
import ReactModal from "react-modal";
import TagManager from "react-gtm-module";
import map from "lodash/map";
import DataLoader from "containers/DataLoader";
import Alert from "components/Alert";
import Helmet from "components/Helmet";
import AuthorizedRoute from "components/AuthorizedRoute";
import PublicRoute from "components/PublicRoute";
import Layout from "components/Layout";
import Spinner, { styles } from "components/Spinner";
import { gotoUrl, handleRedirect, handleRouteLoading } from "utils/helpers";
import AnalyticsFactory from "analytics/AnalyticsFactory";
import { initPushNotification } from "analytics";
import { resetLoadingApis } from "ducks/actions";
import PageError from "containers/PageError";
import {
  PlasmicRootProvider,
  PlasmicComponent
} from "@plasmicapp/loader-react";
import { PLASMIC } from "plasmic-init";
import { GTM_ID, paths } from "utils/constants";
import {
  makeSelectRedirects,
  makeSelectThemeColors,
  makeSelectAlert
} from "ducks/selectors";

// import useMockAPIForDebug from "hooks/useMockAPIForDebug";
// import mockedAppSettingsAPIResponse from "__mocks__/app_settings_api.json";
// import mockedAppSettingsAPIResponse from "__mocks__/phone_only_list.json";

const { ThemeProvider, theme, Global, globalStyles } = DesignCore;
ReactModal.setAppElement("#root");

function App(props) {
  // mocking app_settings api...
  // useMockAPIForDebug("/checkout/app_setting", mockedAppSettingsAPIResponse);
  // useMockAPIForDebug("/checkout/phones", mockedAppSettingsAPIResponse);
  const [routeLoading, setRouteLoading] = useState(true);
  const themeColors = useSelector(makeSelectThemeColors());
  const redirects = useSelector(makeSelectRedirects());
  const alertSetting = useSelector(makeSelectAlert());
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(resetLoadingApis());
    initPushNotification(props.store);
    if (GTM_ID) {
      window.ReactGoogleTagManager = TagManager;
      TagManager.initialize({ gtmId: GTM_ID });
    }
    AnalyticsFactory.pushEvent("page_visit");
    handleRouteLoading(setRouteLoading);
    return history.listen(() => {
      AnalyticsFactory.pushEvent("page_visit");
      handleRouteLoading(setRouteLoading);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    handleRedirect(redirects);
    return history.listen(() => {
      handleRedirect(redirects);
    });
  }, [redirects]);

  /**
   * Renders the route rpovided component encapsulated with few HOCs
   * @param {object} componentProps
   * @param {ReactNode} ComponentToRender
   */
  const renderComponent = (componentProps, ComponentToRender) => {
    window.scrollTo(0, 0);
    return (
      <DataLoader
        {...componentProps}
        render={({ loading, requiresMaintenance }) => {
          if (requiresMaintenance) {
            gotoUrl(paths.MAINTENANCE);
          } else if (loading || routeLoading) {
            return <Spinner size={200} sx={styles.fullPage} />;
          }

          return (
            <>
              <Helmet />
              <ComponentToRender {...componentProps} />
            </>
          );
        }}
      />
    );
  };
  const dynamicTheme = {
    ...theme,
    colors: { ...theme.colors, ...themeColors }
  };

  return (
    <ThemeProvider theme={dynamicTheme}>
      <Global styles={globalStyles} />
      <PlasmicRootProvider loader={PLASMIC}>
        <Router history={history}>
          <Suspense fallback={<Spinner size={200} sx={styles.fullPage} />}>
            <Switch>
              <Route
                path="/plasmic/:page_id"
                exact
                component={CatchAllPlasmicPage}
              />

              {map(routes, ({ path, Component }) => {
                const ComponentToRender = Layout(Component);
                return (
                  <Route
                    key={path}
                    path={path}
                    exact
                    render={props => renderComponent(props, ComponentToRender)}
                  />
                );
              })}

              {map(authorizedRoutes, ({ path, Component, auth }) => {
                const ComponentToRender = Layout(Component);
                return (
                  <AuthorizedRoute
                    key={path}
                    path={path}
                    auth={auth}
                    exact
                    render={props => renderComponent(props, ComponentToRender)}
                  />
                );
              })}

              {map(publicRoutes, ({ path, Component }) => {
                const ComponentToRender = Layout(Component);
                return (
                  <PublicRoute
                    key={path}
                    path={path}
                    exact
                    render={props => renderComponent(props, ComponentToRender)}
                  />
                );
              })}
              <Route path="/:unknown" exact component={PageError} />
              <Route component={PageError} />
            </Switch>
          </Suspense>
        </Router>
      </PlasmicRootProvider>
      <Alert
        stack={{ limit: 1 }}
        html
        position="top"
        effect="stackslide"
        timeout={alertSetting?.timeout || "none"}
      />
    </ThemeProvider>
  );
}

// We try loading the Plasmic page for the current route.
// If it doesn't exist, then return "Not found."
export function CatchAllPlasmicPage() {
  const [loading, setLoading] = useState(true);
  const [pageData, setPageData] = useState(null);
  const { page_id } = useParams();
  const component = page_id || "home";

  useEffect(() => {
    async function load() {
      const pageData = await PLASMIC.maybeFetchComponentData(component);
      setPageData(pageData);
      setLoading(false);
    }
    load();
  }, [component]);

  if (loading) {
    return <Spinner size={200} sx={styles.fullPage} />;
  }
  if (!pageData) {
    return <PageError />;
  }
  // The page will already be cached from the `load` call above.
  return <PlasmicComponent component={component} />;
}

export default App;
